import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Avatar,
  Button,
  Divider,
  Drawer as SidebarDrawer,
  DrawerBody,
  DrawerContent,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Select,
  SelectItem,
} from "@heroui/react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowDownTrayIcon,
  Bars3Icon,
  HomeIcon,
  SparklesIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Logo } from "./logo";
import MainStore from "./MainStore";
import PrimitiveConfig from "./PrimitiveConfig";
import { Icon } from "@iconify/react";
import FeedbackTab from "./@components/FeedbackTab";
import DarkModeToggle from "./DarkModeToggle";

const mainstore = MainStore();

const NAV_ICON_SIZE = "h-4 w-4";

export const ApplicationChromeContext = createContext({
  openAppMenu: () => {},
  closeAppMenu: () => {},
  credits: 0,
  userInfo: undefined,
  organizationName: "",
  onUserAction: () => {},
  appHeaderVisible: true,
  setAppHeaderVisible: () => {},
});

export function useApplicationChrome() {
  return useContext(ApplicationChromeContext);
}

export function AppMenuButton({
  onPress,
  children,
  className,
  variant = "light",
  size = "lg",
  radius = "full",
  isIconOnly = true,
  ...buttonProps
}) {
  const { openAppMenu } = useApplicationChrome();
  const handlePress = onPress ?? openAppMenu;

  return (
    <Button
      isIconOnly={isIconOnly}
      variant={variant}
      size={size}
      radius={radius}
      className={["p-1", className].filter(Boolean).join(" ")}
      onPress={handlePress}
      {...buttonProps}
    >
      {children ?? <Logo />}
    </Button>
  );
}

export function CreditsBadge({
  credits,
  variant = "compact",
  className,
  label,
  radius="lg",
  tone = "light",
  ...wrapperProps
}) {
  const { credits: contextCredits } = useApplicationChrome();
  const value = credits ?? contextCredits ?? 0;
  const classes = (base) => [base, className].filter(Boolean).join(" ");

  if (variant === "card") {
    return (
      <div
        className={classes(
          "border border-default-200 bg-default-100 px-4 py-3 text-sm text-default-600",
          `radius-${radius}`
        )}
        {...wrapperProps}
      >
        <span className="block text-xs uppercase tracking-wide text-default-400">
          {label ?? "Credits available"}
        </span>
        <span className="text-lg font-semibold text-default-700">{value}</span>
      </div>
    );
  }

  const baseClass =
    tone === "dark"
      ? `rounded-${radius} border border-white/30 bg-white/10 px-3 py-1 text-xs text-white/80`
      : `rounded-${radius} border border-default-200 bg-default-100 px-3 py-1 text-xs text-default-500`
  const valueClass =
    tone === "dark"
      ? "font-semibold text-white"
      : "font-semibold text-default-700";

  return (
    <div
      className={classes(baseClass)}
      {...wrapperProps}
    >
      {label ?? "Credits"}:{" "}
      <span className={valueClass}>{value}</span>
    </div>
  );
}

export function UserMenuButton({
  userInfo: userInfoProp,
  organizationName: organizationNameProp,
  onAction,
  renderTrigger,
  placement = "bottom-end",
  menuItems,
  menuProps,
}) {
  const {
    userInfo: contextUserInfo,
    organizationName: contextOrganizationName,
    onUserAction,
  } = useApplicationChrome();

  const userInfo = userInfoProp ?? contextUserInfo ?? {};
  const organizationName = organizationNameProp ?? contextOrganizationName ?? "";
  const handleAction = onAction ?? onUserAction;
  const items =
    menuItems ??
    [
      { key: "account", label: "Account settings" },
      { key: "logout", label: "Log out", color: "danger" },
    ];

  const trigger =
    renderTrigger?.({ userInfo, organizationName }) ?? (
      <Button
        isIconOnly
        variant="light"
        className="border border-default-200"
      >
        <Avatar
          radius="full"
          size="sm"
          referrerPolicy="no-referrer"
          src={userInfo?.avatarUrl}
          className="border border-default-200"
        />
      </Button>
    );

  return (
    <Dropdown placement={placement}>
      <DropdownTrigger>{trigger}</DropdownTrigger>
      <DropdownMenu
        aria-label="Profile actions"
        variant="flat"
        onAction={(key) => handleAction?.(key)}
        {...menuProps}
      >
        {items.map((item) => (
          <DropdownItem key={item.key} color={item.color}>
            {item.label}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </Dropdown>
  );
}

function NavigationButtons({ items, onNavigate, orientation = "column" }) {
  const layoutClass =
    orientation === "row"
      ? "flex-row items-center gap-2"
      : "flex-col gap-2 py-2";

  return (
    <div className={`flex ${layoutClass}`}>
      {items.map((item) => (
        <Button
          key={item.key}
          variant={item.current ? "solid" : "light"}
          color={item.current ? "primary" : "default"}
          startContent={<item.icon className={NAV_ICON_SIZE} />}
          className={orientation === "row" ? "" : "w-full justify-start"}
          onPress={() => onNavigate(item)}
        >
          {item.name}
        </Button>
      ))}
    </div>
  );
}

function WorkspaceSelect({ workspace, workspaces, onChange, size = "md" }) {
  const selectedKeys = workspace ? new Set([String(workspace)]) : new Set();

  const renderVisual = (space, isBordered = true) => {
    if (!space) {
      return (
        <span className="inline-flex h-3 w-3 rounded-full border border-default-200 bg-default-200" />
      );
    }
    const color = space.color || "#2563eb";
    const icon = space.icon;
    const imageUrl = space.imageUrl;
    if (icon) {
      return (
        <Icon
          icon={icon}
          className="h-4 w-4"
          style={{ color }}
          aria-hidden="true"
        />
      );
    }else if (imageUrl) {
      return (
       <Avatar
        isBordered={isBordered}
        className="h-5 w-5 logo"
        src={imageUrl}
      />
      );
    }
    return (
       <div className="flex h-5 inline-flex justify-center place-items-center w-5">
        <span
          className="inline-flex h-3 w-3 rounded-full border border-default-200"
          style={{ backgroundColor: color }}
          aria-hidden="true"
          />
        </div>
    );
  };

  const renderValue = (items) => {
    if (items.length === 0) {
      return <span className="text-default-400">Select workspace</span>;
    }
    const selected = workspaces.find(
      (space) => String(space.id) === String(items[0].key)
    );
    return (
      <div className="flex items-center gap-2">
        {renderVisual(selected, false)}
        <span className="truncate text-sm font-medium text-default-700">
          {selected?.title ?? items[0].textValue}
        </span>
      </div>
    );
  };

  return (
    <Select
      aria-label="Select workspace"
      selectedKeys={selectedKeys}
      className="w-full"
      disallowEmptySelection={workspaces.length > 0}
      selectionMode="single"
      size={size}
      variant="bordered"
      renderValue={renderValue}
      onSelectionChange={(keys) => {
        const [key] = Array.from(keys ?? []);
        if (key) {
          onChange(key);
        }
      }}
    >
      {workspaces.map((space) => (
        <SelectItem
          key={String(space.id)}
          textValue={space.title}
          startContent={renderVisual(space)}
        >
          <div className="flex flex-col">
            <span className="text-sm font-medium text-default-700">
              {space.title}
            </span>
            {space.description && (
              <span className="text-xs text-default-400 truncate">
                {space.description}
              </span>
            )}
          </div>
        </SelectItem>
      ))}
    </Select>
  );
}

function useNavigationItems(workspaceId, locationPath) {
  const navigate = useNavigate();

  return useMemo(() => {
    const baseItems = [
      {
        key: "home",
        name: "Home",
        icon: HomeIcon,
        onClick: () => navigate("/"),
        current: locationPath === "/",
      },
      mainstore.activeWorkspaceId && !mainstore.activeUser?.info?.external
        ? {
            key: "project",
            name: "Project Home",
            icon: SparklesIcon,
            onClick: () => navigate(`/project/${mainstore.activeWorkspaceId}`),
            current: locationPath.startsWith("/project"),
          }
        : null,
      {
        key: "workflows",
        name: "Workflows",
        icon: SparklesIcon,
        onClick: () =>
          navigate(
            mainstore.activeWorkspaceId
              ? `/workflows/${mainstore.activeWorkspaceId}`
              : "/workflows"
          ),
        current: locationPath.startsWith("/workflows"),
      },
      {
        key: "usage",
        name: "Usage",
        icon: ArrowDownTrayIcon,
        onClick: () => navigate("/usage"),
        current: locationPath.startsWith("/usage"),
      },
    ];

    return baseItems.filter(Boolean);
  }, [locationPath, navigate, workspaceId]);
}

export default function ApplicationLayout({
  workspace,
  setWorkspace,
  children,
}) {
  const location = useLocation();
  const routerNavigate = useNavigate();
  const path = location.pathname;

  let layoutPrimitive;
  const itemMatch = path.match(/^\/item\/([^/]+)/);
  if (itemMatch) {
    const rawId = itemMatch[1];
    const parsedId = Number.isNaN(Number(rawId)) ? rawId : Number(rawId);
    layoutPrimitive = mainstore.primitive(parsedId);
  }

  const widePage = useMemo(() => {
    if (path.startsWith("/login") || path.startsWith("/signup")) {
      return false;
    }
    if (
      //path.startsWith("/workflows") ||
      path.startsWith("/usage") ||
      path.startsWith("/account") ||
      path.startsWith("/queue") ||
      path.startsWith("/queues") ||
      path.startsWith("/project")
    ) {
      return false;
    }

    if (layoutPrimitive) {
      const alwaysWideTypes = new Set(["board", "flow", "working", "page"]);
      if (alwaysWideTypes.has(layoutPrimitive.type)) {
        return true;
      }
      if (PrimitiveConfig.pageview?.[layoutPrimitive.type]?.defaultWide) {
        return true;
      }
    }

    return true;
  }, [layoutPrimitive?.id, layoutPrimitive?.type, path]);
  const workspaces = useMemo(() => {
    const ids = mainstore.activeUser?.info?.workspaces ?? [];
    return ids
      .map((id) => mainstore.workspace(id))
      .filter(Boolean)
      .map((space) => ({
        id: space.id,
        title: space.title,
        description: space.metadata?.description,
        icon: space.metadata?.icon || space.icon,
        imageUrl: space.logoUrl,
        color:
          space.metadata?.color || space.color || space.themeColor || "#2563eb",
      }));
  }, [mainstore.activeUser?.info?.workspaces]);

  const navigationItems = useNavigationItems(workspace, location.pathname);
  const isFullWidth = !!widePage;
  const showStaticSidebar = !isFullWidth;
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const hideAppHeader =
    path.startsWith("/workflow/") ||
    path.startsWith("/flowinstance/") ||
    path.startsWith("/published/new_instance/") ||
    layoutPrimitive?.type === "flowinstance";
  const [appHeaderVisible, setAppHeaderVisible] = useState(true);

  const handleNavigate = useCallback(
    (item, closeDrawer) => {
      item.onClick?.();
      closeDrawer?.();
      setIsDrawerOpen(false);
    },
    []
  );

  const handleWorkspaceChange = useCallback(
    (id, closeDrawer) => {
      setWorkspace(id);
      routerNavigate(`/project/${mainstore.activeWorkspaceId}`)
      closeDrawer?.();
      setIsDrawerOpen(false);
    },
    [setWorkspace]
  );

  const handleUserAction = useCallback(
    (key, closeDrawer) => {
      if (key === "account") {
        routerNavigate("/account");
      } else if (key === "logout") {
        window.location.href = "/logout";
      }
      closeDrawer?.();
      setIsDrawerOpen(false);
    },
    [routerNavigate]
  );

  useEffect(() => {
    setIsDrawerOpen(false);
  }, [isFullWidth]);

  const creditsAvailable = mainstore.activeOrganization?.credits ?? 0;
  const userInfo = mainstore.activeUser?.info;
  const organizationName = mainstore.activeOrganization?.name ?? "";

  const openAppMenu = useCallback(() => setIsDrawerOpen(true), [setIsDrawerOpen]);
  const closeAppMenu = useCallback(() => setIsDrawerOpen(false), [setIsDrawerOpen]);

  useEffect(() => {
    setAppHeaderVisible(true);
  }, [path]);

  const chromeContextValue = useMemo(
    () => ({
      openAppMenu,
      closeAppMenu,
      credits: creditsAvailable,
      userInfo,
      organizationName,
      onUserAction: (key) => handleUserAction(key),
      appHeaderVisible,
      setAppHeaderVisible,
    }),
    [
      openAppMenu,
      closeAppMenu,
      creditsAvailable,
      userInfo,
      organizationName,
      handleUserAction,
      appHeaderVisible,
      setAppHeaderVisible,
    ]
  );

  const renderNavigation = (closeDrawer) => (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-2 px-6 py-5">
        <div className="flex items-center gap-2">
          <Logo className="h-9 w-9" />
           <p className="font-['Poppins'] font-black font-family-[Poppins] text-xl lg:text-2xl">SENSE</p>
        </div>
        {closeDrawer && (
          <Button
            isIconOnly
            variant="light"
            size="sm"
            onPress={closeDrawer}
            className="shadow-sm"
          >
            <XMarkIcon className="h-5 w-5" />
          </Button>
        )}
      </div>
      <div className="px-6 pb-4 space-y-4">
        <WorkspaceSelect
          workspace={workspace}
          workspaces={workspaces}
          onChange={(id) => handleWorkspaceChange(id, closeDrawer)}
        />
      </div>
      <Divider />
      <div className="flex-1 overflow-y-auto px-4 pb-6">
        <NavigationButtons
          items={navigationItems}
          onNavigate={(item) => handleNavigate(item, closeDrawer)}
        />
      </div>
      <Divider />
      <div className="px-6 py-4">
        <CreditsBadge variant="card" credits={creditsAvailable} />
      </div>
      <div className="border-t border-default-200 px-6 py-5">
        <UserMenuButton
          placement="top"
          onAction={(key) => handleUserAction(key, closeDrawer)}
          renderTrigger={({ userInfo, organizationName }) => (
            <Button variant="light" className="w-full justify-start gap-3">
              <Avatar
                radius="full"
                size="sm"
                src={userInfo?.avatarUrl}
                referrerPolicy="no-referrer"
                className="border border-default-200"
              />
              <div className="min-w-0 text-left">
                <p className="truncate text-sm font-medium text-default-700">
                  {userInfo?.name ?? "User"}
                </p>
                <p className="truncate text-xs text-default-400">
                  {organizationName ?? ""}
                </p>
              </div>
            </Button>
          )}
          menuProps={{ variant: "flat" }}
        />
      </div>
    </div>
  );

  const outletElement = children ?? <Outlet context={{ widePage }} />;

  return (
    <ApplicationChromeContext.Provider value={chromeContextValue}>
      <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-950">
        <FeedbackTab/>
        {showStaticSidebar && (
          <aside className="hidden h-full w-72 flex-shrink-0 flex-col border-r border-divider bg-background lg:flex">
            {renderNavigation(null)}
          </aside>
        )}

        <SidebarDrawer
          placement="left"
          isOpen={isDrawerOpen}
          onOpenChange={setIsDrawerOpen}
          hideCloseButton
          backdrop="blur"
          size="sm"
          classNames={{
            base: "h-full max-h-none w-72 max-w-none rounded-none border-r border-divider bg-background",
            wrapper: "z-[60]",
          }}
        >
          <DrawerContent>
            {(onClose) => (
              <DrawerBody className="p-0">
                {renderNavigation(onClose)}
              </DrawerBody>
            )}
          </DrawerContent>
        </SidebarDrawer>

        <div className="relative flex min-w-0 flex-1 flex-col overflow-hidden">
          {(!hideAppHeader && appHeaderVisible) &&
            (isFullWidth ? (
              <header className="sticky top-0 z-40 flex h-14 w-full items-center justify-between border-b border-default-200 bg-white/90 px-2 dark:border-gray-800 dark:bg-gray-900/80">
                <div className="flex items-center space-x-2 h-full">
                  <AppMenuButton />
                  <Divider orientation="vertical" />
                  <div className="flex flex-col space-y-0.5 pl-1">
                    {layoutPrimitive && (
                      <>
                        <span className="text-lg font-bold text-default-700">
                          {layoutPrimitive?.title ?? ""}
                        </span>
                        <span className="text-sm font-medium text-default-500">
                          {layoutPrimitive?.displayType} #{layoutPrimitive?.plainId ?? ""}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <DarkModeToggle />
                  <CreditsBadge />
                  <UserMenuButton />
                </div>
              </header>
            ) : (
              <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-default-200 bg-white/90 px-4 backdrop-blur lg:hidden dark:border-gray-800 dark:bg-gray-900/80">
                <AppMenuButton
                  size="sm"
                  className="border border-default-200 p-0"
                >
                  <Bars3Icon className="h-5 w-5" />
                </AppMenuButton>
                <span className="text-sm font-semibold text-default-700">Sense</span>
                <div className="flex items-center gap-2">
                  <DarkModeToggle size="sm" />
                  <UserMenuButton />
                </div>
              </header>
            ))}

          <main className="flex-1 overflow-hidden bg-gray-100 dark:bg-gray-950">{outletElement}</main>
        </div>
      </div>
    </ApplicationChromeContext.Provider>
  );
}
